import { solicitudActividadService } from './solicitudActividadService';
import { supabase, Produccion, ProductoActividad } from '../lib/supabase';

// Interfaz para crear una producción (tanda de un producto)
interface CreateProduccionData {
  actividad_id: string;
  producto_id: string;
  nombre?: string;
  stock_inicial: number;
  fecha_inicio?: string;
  fecha_fin?: string;
}

// Interfaz para actualizar una producción
interface UpdateProduccionData {
  nombre?: string;
  stock_inicial?: number;
  fecha_inicio?: string | null;
  fecha_fin?: string | null;
  estado?: 'activa' | 'finalizada';
}

// Interfaz para métricas de producción
export interface ProduccionConMetricas extends Produccion {
  total_ingresos: number;
  total_costos: number;
  utilidad_real: number;
  utilidad_objetivo_calculada: number;
  stock_disponible: number;
  cantidad_pedidos: number;
  total_vendido: number;
  margen_utilidad: number;
  producto?: ProductoActividad;
}

export const produccionService = {
  // Obtener todas las producciones de una actividad
  async getByActividad(actividadId: string): Promise<Produccion[]> {
    const { data, error } = await supabase
      .from('producciones')
      .select('*')
      .eq('actividad_id', actividadId)
      .order('numero', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Obtener una producción por ID
  async getById(id: string): Promise<Produccion | null> {
    const { data, error } = await supabase
      .from('producciones')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  // Crear una nueva producción (tanda de un producto)
  async create(data: CreateProduccionData): Promise<Produccion> {
    const id = crypto.randomUUID();
    const datos = { ...data, fecha_inicio: data.fecha_inicio || null, fecha_fin: data.fecha_fin || null };
    return solicitudActividadService.ejecutarContrato<Produccion>('crear_produccion',
      { p_id: id, p_datos: datos }, { id, datos });
  },

  // Actualizar una producción
  async update(id: string, cambios: UpdateProduccionData): Promise<Produccion> {
    return solicitudActividadService.ejecutarContrato<Produccion>('editar_produccion',
      { p_id: id, p_cambios: cambios }, { id, cambios });
  },

  // Eliminar una producción
  async delete(id: string): Promise<void> {
    await solicitudActividadService.ejecutarContrato('eliminar_produccion', { p_id: id }, { id });
  },

  // Obtener producciones con métricas calculadas
  async getByActividadConMetricas(actividadId: string): Promise<ProduccionConMetricas[]> {
    // Obtener producciones con datos del producto
    const { data: producciones, error } = await supabase
      .from('producciones')
      .select(`
        *,
        producto:productos_actividad(*)
      `)
      .eq('actividad_id', actividadId)
      .order('numero', { ascending: true });

    if (error) throw error;
    if (!producciones || producciones.length === 0) return [];

    // Obtener métricas para cada producción
    const produccionesConMetricas: ProduccionConMetricas[] = [];

    for (const produccion of producciones) {
      // Contar pedidos de esta producción
      const { count: cantidadPedidos } = await supabase
        .from('ventas_actividad')
        .select('*', { count: 'exact', head: true })
        .eq('produccion_id', produccion.id)
        .eq('anulado', false);

      // Sumar ingresos y cantidades de esta producción
      const { data: ventasData } = await supabase
        .from('ventas_actividad')
        .select('monto_pagado, cantidad, cantidad_entregada')
        .eq('produccion_id', produccion.id)
        .eq('anulado', false);

      const totalIngresos = ventasData?.reduce((sum, v) => sum + (v.monto_pagado || 0), 0) || 0;
      const totalVendido = ventasData?.reduce((sum, v) => sum + (v.cantidad || 0), 0) || 0;

      // Sumar costos de esta producción
      const { data: costosData } = await supabase
        .from('costos_actividad')
        .select('monto')
        .eq('produccion_id', produccion.id)
        .eq('anulado', false);

      const totalCostos = costosData?.reduce((sum, c) => sum + (c.monto || 0), 0) || 0;

      // Calcular utilidad objetivo = (precio × stock_inicial) - costos
      const precio = produccion.producto?.precio_unitario || 0;
      const ingresoMaximo = precio * produccion.stock_inicial;
      const utilidadObjetivoCalculada = ingresoMaximo - totalCostos;

      // Stock disponible = stock_inicial - vendido
      const stockDisponible = produccion.stock_inicial - totalVendido;

      const margenUtilidad = totalIngresos > 0 ? ((totalIngresos - totalCostos) / totalIngresos) * 100 : 0;

      produccionesConMetricas.push({
        ...produccion,
        total_ingresos: totalIngresos,
        total_costos: totalCostos,
        utilidad_real: totalIngresos - totalCostos,
        utilidad_objetivo_calculada: utilidadObjetivoCalculada,
        stock_disponible: stockDisponible,
        cantidad_pedidos: cantidadPedidos || 0,
        total_vendido: totalVendido,
        margen_utilidad: Math.round(margenUtilidad * 100) / 100,
        producto: produccion.producto
      });
    }

    return produccionesConMetricas;
  },

  // Obtener producciones de un producto específico
  async getByProducto(productoId: string): Promise<Produccion[]> {
    const { data, error } = await supabase
      .from('producciones')
      .select('*')
      .eq('producto_id', productoId)
      .order('numero', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Obtener producciones agrupadas por producto para una actividad
  async getByActividadAgrupadasPorProducto(actividadId: string): Promise<Map<string, ProduccionConMetricas[]>> {
    const producciones = await this.getByActividadConMetricas(actividadId);
    
    const agrupadas = new Map<string, ProduccionConMetricas[]>();
    
    for (const produccion of producciones) {
      const productoId = produccion.producto_id || 'sin_producto';
      if (!agrupadas.has(productoId)) {
        agrupadas.set(productoId, []);
      }
      agrupadas.get(productoId)!.push(produccion);
    }
    
    return agrupadas;
  }
};
